import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaChevronDown, FaChevronUp, FaMapMarkerAlt } from 'react-icons/fa';
import './Product.css';

const Product = ({ translations }) => {
  const { slug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [coverImageAvailable, setCoverImageAvailable] = useState(true);
  const [galleryItems, setGalleryItems] = useState([]);
  const maxImagesToScan = 60;

  const objectTranslations = translations?.components?.products?.translations || {};
  const productKeyBase = `products.${slug}`;

  const productTitleText = objectTranslations[`${productKeyBase}.title`];
  const productBodyText = objectTranslations[`${productKeyBase}.body`];
  const productAddressText = objectTranslations[`${productKeyBase}.address`];
  const productFeature1Text = objectTranslations[`${productKeyBase}.feature.1`];
  const productFeature2Text = objectTranslations[`${productKeyBase}.feature.2`];
  const productFeature3Text = objectTranslations[`${productKeyBase}.feature.3`];
  const productFeature4Text = objectTranslations[`${productKeyBase}.feature.4`];
  const productFeatures = [
    productFeature1Text,
    productFeature2Text,
    productFeature3Text,
    productFeature4Text,
  ].filter((item) => item && String(item).trim());
  const productAddressMapsUrl = productAddressText
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(productAddressText)}`
    : '';

  const getImage = (n) => {
    return `/img/products/${slug}/foto${n}.png`;
  };

  const photoInitialParam = searchParams.get('photoInitial');
  const coverPhotoMatch = photoInitialParam ? photoInitialParam.match(/^foto(\d+)$/i) : null;
  const coverPhotoIndex = coverPhotoMatch ? Number(coverPhotoMatch[1]) : null;
  const coverImageSrc =
    coverPhotoIndex && coverPhotoIndex >= 1
      ? getImage(coverPhotoIndex)
      : null;
  const shouldRenderCover = Boolean(coverImageSrc && coverImageAvailable);

  useEffect(() => {
    setCoverImageAvailable(true);
  }, [slug, photoInitialParam]);

  useEffect(() => {
    let cancelled = false;

    const checkImageExists = (src) =>
      new Promise((resolve) => {
        const image = new Image();
        image.onload = () => resolve(true);
        image.onerror = () => resolve(false);
        image.src = src;
      });

    const loadGallery = async () => {
      const imageChecks = await Promise.all(
        Array.from({ length: maxImagesToScan }, (_, i) => {
          const index = i + 1;
          const src = getImage(index);
          return checkImageExists(src).then((exists) => (exists ? { index, src } : null));
        })
      );

      if (cancelled) return;

      const availableItems = imageChecks.filter(Boolean);
      setGalleryItems(availableItems);
      setLightboxIndex((prev) => {
        if (prev === null) return null;
        return availableItems.some((item) => item.index === prev)
          ? prev
          : (availableItems[0]?.index ?? null);
      });
    };

    setGalleryItems([]);
    setLightboxIndex(null);
    loadGallery();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const goPrev = () => {
    setLightboxIndex((prev) => {
      if (prev === null || galleryItems.length === 0) return prev;
      const currentPos = galleryItems.findIndex((item) => item.index === prev);
      if (currentPos === -1) return galleryItems[0].index;
      const prevPos = (currentPos - 1 + galleryItems.length) % galleryItems.length;
      return galleryItems[prevPos].index;
    });
  };

  const goNext = () => {
    setLightboxIndex((prev) => {
      if (prev === null || galleryItems.length === 0) return prev;
      const currentPos = galleryItems.findIndex((item) => item.index === prev);
      if (currentPos === -1) return galleryItems[0].index;
      const nextPos = (currentPos + 1) % galleryItems.length;
      return galleryItems[nextPos].index;
    });
  };

  return (
    <div className="product-page">
      <header className="product-header">
        <div className="product-header-inner">
          <Link to="/" className="product-back">
            <div className="sticky-logo">
              <h1>
                <img src="/img/san-partners-black.svg" alt="" className="logo-img" />
                <span className="brand-name">San &amp; </span> Partners
              </h1>
            </div>
          </Link>
        </div>
      </header>

      <section className="product-content area-padding">
        <div className="container product-content-centered">
          {shouldRenderCover ? (
            <div className="product-cover">
              <img
                src={coverImageSrc}
                alt={productTitleText || slug}
                className="product-cover-image"
                onError={() => setCoverImageAvailable(false)}
              />
              {productTitleText && (
                <div className="product-cover-overlay">
                  <h2>{productTitleText}</h2>
                </div>
              )}
            </div>
          ) : (
            productTitleText && (
              <div className="section-headline text-center">
                <h2>{productTitleText}</h2>
              </div>
            )
          )}
          {productFeatures.length > 0 && (
            <div className="product-feature-balloons">
              {productFeatures.map((feature, index) => (
                <span key={index} className="product-feature-balloon">{feature}</span>
              ))}
            </div>
          )}
          {productBodyText && (
            <div className="product-details-wrap">
              <button
                type="button"
                className="product-details-toggle"
                onClick={() => setDetailsOpen((prev) => !prev)}
                aria-expanded={detailsOpen}
              >
                {detailsOpen ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              <div className={`product-body ${detailsOpen ? 'is-open' : 'is-closed'}`}>{productBodyText}</div>
            </div>
          )}
          {productAddressText && (
            <p className="product-address">
              <a
                className="product-address-link"
                href={productAddressMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaMapMarkerAlt className="product-address-icon" />
                <span className="product-address-text">{productAddressText}</span>
              </a>
            </p>
          )}
        </div>
      </section>

      {galleryItems.length > 0 && (
        <section className="product-gallery-section area-padding">
          <div className="container">
            <div className="product-gallery">
              {galleryItems.map(({ index, src }) => (
                <button key={index} type="button" className="product-gallery-item" onClick={() => setLightboxIndex(index)}>
                  <img src={src} alt="" />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {lightboxIndex !== null && (
        <div className="product-lightbox" onClick={() => setLightboxIndex(null)} role="button" tabIndex={0}>
          <button
            type="button"
            className="product-lightbox-close"
            onClick={() => setLightboxIndex(null)}
          >
            X
          </button>
          <img src={getImage(lightboxIndex)} alt="" onClick={(e) => e.stopPropagation()} />
          <button
            type="button"
            className="product-lightbox-nav product-lightbox-nav-prev"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
          >
            <FaChevronLeft className="product-lightbox-nav-icon" />
          </button>
          <button
            type="button"
            className="product-lightbox-nav product-lightbox-nav-next"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
          >
            <FaChevronRight className="product-lightbox-nav-icon" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Product;
